import type { ClientId } from '../core/tokens/environment';

export type AuthVisualVariant = 'canvas' | 'image';
export type AuthLogoVariant = 'reach-link' | 'icon';
export type SideNavLogoVariant = 'logo-with-text-image' | 'icon-with-title';
export type LoaderLogoVariant = 'rings' | 'lottie';
export type InitLoaderVariant = 'image' | 'lottie';
export type InitThemeVariant = 'default' | 'olympics';

export interface ClientConfig {
	/** Tenant identifier selected at build-time (environment.clientId). */
	id: ClientId;

	/** Visual/branding decisions that can vary by tenant. */
	branding: {
		/** Browser title / app name for this tenant. */
		appTitle: string;
		/** Title shown in admin dashboard navigation. */
		adminTitle: string;
		/** Variant for the visual panel in auth flows (login/register/email-sent). */
		authVisualVariant: AuthVisualVariant;
		/** Top logo shown in auth forms (link logo vs icon-only mark). */
		authLogoVariant: AuthLogoVariant;
		/** Header logo style used in home side navigation. */
		sideNavLogoVariant: SideNavLogoVariant;
		/**
		 * Main tenant mark in the shared icon font (`@reach/icons` main-icons).
		 * Default: `reach.svg` → `icon-reach`; IOC: `olympic-assistant-mark.svg`.
		 */
		tenantMarkIconClass: string;
		/** App loaders (startup + generic loading indicator) visual policy. */
		loader: {
			/** Loader used by <reach-logo-loader>. */
			logoVariant: LoaderLogoVariant;
			/** Lottie path for logo loader when logoVariant = 'lottie'. */
			logoLottiePath?: string;
			/** Startup screen loader style in app-initialization. */
			initVariant: InitLoaderVariant;
			/** Lottie path for startup loader when initVariant = 'lottie'. */
			initLottiePath?: string;
			/** Theme token used by app-initialization styles. */
			initTheme: InitThemeVariant;
		};
	};

	/** Functional behaviour flags/rules that vary by tenant. */
	features: {
		integrations: {
			/** Whether Microsoft Teams setup is required for integration flows. */
			teamsRequired: boolean;
			/** Whether Google Drive integration is enabled. */
			googleDriveIntegrationEnabled: boolean;
			/** Whether Google Docs integration is enabled. */
			googleDocsIntegrationEnabled: boolean;
		};
		authentication: {
			/** If false, register page/self-sign-up is hidden/disabled. */
			allowSelfServiceRegistration: boolean;
			/** Enable Microsoft login button and auth flow. */
			allowMicrosoftLogin: boolean;
			/** Enable Google login button and auth flow. */
			allowGoogleLogin: boolean;
			/** Sign in with Apple (web + iOS native). */
			allowAppleLogin: boolean;
			/** Google client id to use for web/native sign-in (if Google login is enabled). */
			googleSignInClientId?: string;
		};
	};

	/** myGenie (widget admin, chat widget) behaviour per tenant. */
	myGenie: {
		widgetSettings: {
			/** Integration UI blocks on the my-genie widget integrations page. */
			integrations: {
				/** Microsoft Teams connect / manage block. */
				showMicrosoftTeams: boolean;

				/** Slack Teams connect / manage block. */
				showSlackTeams: boolean;
			};
		};
	};

	/** Tenant-specific copy/content toggles not tied to app behaviour rules. */
	content: {
		/** Show IOC-style disclaimer block in widget screens. */
		showDisclaimerInWidget: boolean;
	};
}

export const DEFAULT_CLIENT_CONFIG: ClientConfig = {
	id: 'default',
	branding: {
		appTitle: 'myReach',
		adminTitle: 'myReach Admin',
		authVisualVariant: 'canvas',
		authLogoVariant: 'reach-link',
		sideNavLogoVariant: 'logo-with-text-image',
		tenantMarkIconClass: 'icon-reach',
		loader: {
			logoVariant: 'rings',
			logoLottiePath: '/assets/animations/myreach_load.json',
			initVariant: 'image',
			initTheme: 'default',
		},
	},
	features: {
		integrations: {
			teamsRequired: false,
			googleDriveIntegrationEnabled: true,
			googleDocsIntegrationEnabled: true,
		},
		authentication: {
			allowSelfServiceRegistration: true,
			allowMicrosoftLogin: true,
			allowGoogleLogin: true,
			allowAppleLogin: true,
			googleSignInClientId:
				'106184129344-iphm6q5ab9tvvhq00gvmsb3f16kie2uo.apps.googleusercontent.com',
		},
	},
	myGenie: {
		widgetSettings: {
			integrations: {
				showMicrosoftTeams: false,
				showSlackTeams: false,
			},
		},
	},
	content: {
		showDisclaimerInWidget: false,
	},
};

export const IOC_CLIENT_CONFIG: ClientConfig = {
	id: 'ioc',
	branding: {
		appTitle: 'Olympic Assistant',
		adminTitle: 'Olympic Admin',
		authVisualVariant: 'image',
		authLogoVariant: 'icon',
		sideNavLogoVariant: 'icon-with-title',
		tenantMarkIconClass: 'icon-olympic-assistant-mark',
		loader: {
			logoVariant: 'lottie',
			logoLottiePath: '/assets/animations/ioc_animation.json',
			initVariant: 'lottie',
			initLottiePath: '/assets/animations/Olympic_Rings-to-AI.json',
			initTheme: 'olympics',
		},
	},
	features: {
		integrations: {
			teamsRequired: true,
			googleDriveIntegrationEnabled: false,
			googleDocsIntegrationEnabled: false,
		},
		authentication: {
			allowSelfServiceRegistration: false,
			allowMicrosoftLogin: true,
			allowGoogleLogin: false,
			allowAppleLogin: false,
		},
	},
	myGenie: {
		widgetSettings: {
			integrations: {
				showMicrosoftTeams: false,
				showSlackTeams: false,
			},
		},
	},
	content: {
		showDisclaimerInWidget: true,
	},
};

export const CLIENT_CONFIG_MAP: Record<ClientId, ClientConfig> = {
	default: DEFAULT_CLIENT_CONFIG,
	ioc: IOC_CLIENT_CONFIG,
};

export function getClientConfig(clientId: ClientId): ClientConfig {
	return CLIENT_CONFIG_MAP[clientId];
}
